/* ---------------------------------------------------------------------------
 * Admin i18n resources — eager English, lazy active locale.
 *
 * Admin only supports the 6 ADMIN_LOCALES (en, de, fr, es, tr, ja) × 2
 * namespaces (common, admin). We bundle English eagerly (default + fallbackLng)
 * and lazy-load the other 5 locales on demand so their JSON is code-split out
 * of the main chunk. `useSuspense:false` renders the English fallback until the
 * active locale chunk resolves.
 * --------------------------------------------------------------------------*/
import type { i18n as I18n } from 'i18next';
import { isSupportedLocale, ADMIN_LOCALES, DEFAULT_LOCALE } from '@noxverify/i18n';

import enCommon from '../../../packages/i18n/locales/en/common.json';
import enAdmin from '../../../packages/i18n/locales/en/admin.json';

/** Eager English resources used to initialise i18next synchronously. */
export const eagerEnResources = {
  en: {
    common: enCommon as Record<string, unknown>,
    admin: enAdmin as Record<string, unknown>,
  },
};

// Lazy modules restricted to the admin locale set; Vite emits one chunk each.
const lazyCommon = import.meta.glob<{ default: Record<string, unknown> }>(
  '../../../packages/i18n/locales/{de,fr,es,tr,ja}/common.json',
);
const lazyAdmin = import.meta.glob<{ default: Record<string, unknown> }>(
  '../../../packages/i18n/locales/{de,fr,es,tr,ja}/admin.json',
);

function loaderFor(
  modules: Record<string, () => Promise<{ default: Record<string, unknown> }>>,
  locale: string,
): (() => Promise<{ default: Record<string, unknown> }>) | undefined {
  const suffix = `/locales/${locale}/`;
  for (const [path, load] of Object.entries(modules)) {
    if (path.includes(suffix)) return load;
  }
  return undefined;
}

const loaded = new Set<string>([DEFAULT_LOCALE]);

/** Add the `common`+`admin` bundles for `locale`, then re-render dependents. */
export async function ensureLocaleLoaded(i18n: I18n, locale: string): Promise<void> {
  if (
    loaded.has(locale) ||
    !isSupportedLocale(locale) ||
    !ADMIN_LOCALES.includes(locale)
  ) {
    return;
  }
  loaded.add(locale);

  const [commonMod, adminMod] = await Promise.all([
    loaderFor(lazyCommon, locale)?.(),
    loaderFor(lazyAdmin, locale)?.(),
  ]);

  if (commonMod) i18n.addResourceBundle(locale, 'common', commonMod.default, true, true);
  if (adminMod) i18n.addResourceBundle(locale, 'admin', adminMod.default, true, true);

  if (i18n.resolvedLanguage === locale || i18n.language === locale) {
    i18n.emit('languageChanged', locale);
  }
}

/** Load the active locale now and on every `languageChanged`. */
export function attachLazyLocaleLoader(i18n: I18n): () => void {
  const onChange = (lng: string) => {
    void ensureLocaleLoaded(i18n, lng);
  };
  i18n.on('languageChanged', onChange);
  const current = i18n.resolvedLanguage ?? i18n.language;
  if (current) void ensureLocaleLoaded(i18n, current);
  return () => i18n.off('languageChanged', onChange);
}
